import { useCallback, useEffect, useState } from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Caption,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  useImpersonation,
  useImpersonationTimeRemaining,
  useSession,
} from '@eluve/session-helpers';

export const ImpersonationExpiryMonitor = () => {
  const session = useSession();
  const {
    isImpersonating,
    isLoading,
    renewImpersonation,
    exitImpersonation,
    expiresAt,
    impersonationOriginUrl,
  } = useImpersonation();
  const toast = useToast();

  const [showModal, setShowModal] = useState(false);
  const [cannotRenew, setCannotRenew] = useState(false);
  const [autoExitCountdown, setAutoExitCountdown] = useState<number | null>(
    null,
  );

  const timeRemaining = useImpersonationTimeRemaining(expiresAt, 'short');

  const currentUserName = session.firstName
    ? `${session.firstName} ${session.lastName}`.trim()
    : session.email;

  const handleRenew = useCallback(async () => {
    try {
      await renewImpersonation();
      setShowModal(false);
      setCannotRenew(false);
    } catch {
      toast.error('Failed to renew impersonation');
      setCannotRenew(true);
    }
  }, [renewImpersonation, toast]);

  const handleExit = useCallback(async () => {
    try {
      await exitImpersonation(impersonationOriginUrl);
    } catch {
      toast.error('Failed to exit impersonation');
    }
  }, [exitImpersonation, impersonationOriginUrl, toast]);

  useEffect(() => {
    if (!isImpersonating || !expiresAt) {
      setShowModal(false);
      setCannotRenew(false);
      setAutoExitCountdown(null);
      return;
    }

    const checkExpiry = () => {
      const timeLeft = expiresAt.getTime() - Date.now();
      const isExpiringSoon = timeLeft < 2 * 60 * 1000;
      const isExpired = timeLeft <= 0;

      if (isExpiringSoon) {
        setShowModal(true);
        if (isExpired && autoExitCountdown === null) {
          setAutoExitCountdown(15);
        }
      } else {
        setShowModal(false);
        setAutoExitCountdown(null);
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 1000);
    return () => clearInterval(interval);
  }, [isImpersonating, expiresAt, autoExitCountdown]);

  useEffect(() => {
    if (cannotRenew && autoExitCountdown === null) {
      setAutoExitCountdown(15);
    }
  }, [cannotRenew, autoExitCountdown]);

  useEffect(() => {
    if (autoExitCountdown === null) return;

    if (autoExitCountdown === 0) {
      handleExit();
      return;
    }

    const timer = setTimeout(() => {
      setAutoExitCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [autoExitCountdown, handleExit]);

  const isExpired = expiresAt && expiresAt.getTime() <= Date.now();

  const title = isExpired
    ? 'Impersonation Session Expired'
    : 'Impersonation Session Expiring Soon';

  const timeRemainingDisplay = timeRemaining
    ? ` (${timeRemaining} remaining)`
    : 'soon';

  const description =
    isExpired || cannotRenew
      ? autoExitCountdown !== null
        ? `Your impersonation session has expired. Returning to admin view in ${autoExitCountdown} seconds...`
        : 'Your impersonation session has expired or cannot be renewed. Please exit impersonation to return to your admin account.'
      : `Your impersonation session will expire ${timeRemainingDisplay}. Would you like to renew the session or exit impersonation?`;

  return (
    <AlertDialog open={showModal}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Stack gap={2} direction="horizontal" align="center">
              <Icon
                name="AlertTriangle"
                size="lg"
                className="text-text-yellow-strong"
              />
              {title}
            </Stack>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <Stack gap={3}>
              <Text size="xs" color="secondary">
                {description}
              </Text>

              <Stack className="rounded-md bg-bg-secondary p-3" gap={2}>
                <Stack gap={2} direction="horizontal" align="center">
                  <Caption inline size="3xs" color="tertiary">
                    Currently impersonating:
                  </Caption>
                </Stack>
                <Caption size="3xs">
                  <span className="font-semibold">{currentUserName}</span>
                  {currentUserName !== session.email && ` (${session.email})`}
                </Caption>

                {timeRemaining && !isExpired && (
                  <Stack
                    gap={1}
                    className="mt-2"
                    direction="horizontal"
                    align="center"
                  >
                    <Icon
                      name="Clock"
                      size="xs"
                      className="text-text-yellow-strong"
                    />
                    <Caption inline size="3xs" color="secondary">
                      {timeRemaining} remaining
                    </Caption>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {isExpired || cannotRenew ? (
            <Button onClick={handleExit} disabled={isLoading} fullWidth>
              <Icon name="AlertTriangle" size="auto" />
              {isLoading
                ? 'Exiting...'
                : autoExitCountdown !== null
                  ? 'Return Now'
                  : 'Exit Impersonation'}
            </Button>
          ) : (
            <Stack gap={2} direction="horizontal">
              <Button
                variant="outline"
                onClick={handleRenew}
                disabled={isLoading}
                className="flex-1"
              >
                {!isLoading && <Icon name="RefreshCw" size="auto" />}
                {isLoading ? 'Renewing...' : 'Renew Session'}
              </Button>
              <Button
                variant="outline"
                onClick={handleExit}
                disabled={isLoading}
                className="flex-1"
              >
                <Icon name="AlertTriangle" size="auto" />
                {isLoading ? 'Exiting...' : 'Exit Impersonation'}
              </Button>
            </Stack>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
